/* eslint-disable import/prefer-default-export */
import { LocationProps } from '@models/navigation';
import { supportedLocales } from './config.services';
import {
  getMonthNumAndAbbrevArrByLocale,
  toTitleCase,
} from './utilities.services';

const removeTrailingSlash = (url: string) => (url.endsWith('/') ? url.slice(0, -1) : url);

const getEnglishPagePath = (
  locationData: any,
  lang: string,
  localesArr: any,
) => {
  if (lang === 'en') return removeTrailingSlash(locationData.pathname);
  let englishPathPage = '';
  localesArr.forEach((locale: any) => {
    if (locale.lang === 'en') englishPathPage = locale.slug;
  });
  return removeTrailingSlash(englishPathPage);
};

const getEnglishPageUrl = (
  locationData: any,
  lang: string,
  localesArr: any,
) => {
  if (lang === 'en') return removeTrailingSlash(locationData.href);
  let englishPagePath = '';
  localesArr.forEach((locale: any) => {
    if (locale.lang === 'en') {
      englishPagePath = locale.slug;
    }
  });
  const englishPageUrl = `${locationData.protocol}//${locationData.hostname}${englishPagePath}${locationData.search}`;
  return removeTrailingSlash(englishPageUrl);
};

const getPageTitleFromPathname = (splitPathname: any) => {
  let title: string = '';
  if (!splitPathname) return title;
  title = splitPathname?.filter((part: string) => part !== '').at(-1);
  if (!title) return '';
  title = toTitleCase(title.replaceAll('-', ' '));
  title = title.replaceAll('Chd-Tv', 'CHD-TV');
  title += ' | Childrens Health Defense';
  return title;
};

const getPageTitle = (
  locationData: LocationProps,
) => {
  let title: string = '';
  title = document?.title;
  if (!title) {
    // get from URL as fallback
    title = getPageTitleFromPathname(locationData.pathname.split('/'));
  }
  return title;
};

const getContentTitle = () => {
  let title: string = '';
  // get content title on video pages for chd-tv
  if (document.getElementById('videoContainer')) {
    title = document.getElementById('content-title')?.textContent ?? '';
  }
  return title;
};

const getContentTitleLength = () => {
  const contentTitle = getContentTitle();
  if (contentTitle === null) return contentTitle;
  const contentTitleLength: number = contentTitle.length;
  if (contentTitleLength === 0) return '';
  return contentTitleLength;
};

const getSiteSection = (
  sectionNum: number,
  splitPathname: any,
  lang: string,
) => {
  let siteSection: string = '';
  if (!splitPathname) return siteSection;
  const sections = splitPathname.filter((part: string) => part !== '');
  if (lang === 'en') {
    if (sectionNum === 2) {
      if (sections.length === 1) {
        siteSection = 'Home';
      } else {
        [, siteSection] = sections;
        siteSection = toTitleCase(siteSection?.replaceAll('-', ' ')).replaceAll('Chd', 'CHD');
      }
    }
    if (sectionNum === 3) {
      const parentContentTitle = document.getElementById('parent-content-title')?.textContent ?? '';
      if (parentContentTitle !== '') {
        siteSection = parentContentTitle;
      } else {
        [, , , siteSection] = sections;
        siteSection = toTitleCase(siteSection?.replaceAll('-', ' ')).replaceAll('Chd', 'CHD');
      }
    }
  } else {
    // this accounts for the '/{lang}/' in url
    if (sectionNum === 2) {
      if (sections.length === 2) {
        siteSection = 'Home';
      } else {
        [, , siteSection] = sections;
        siteSection = toTitleCase(siteSection?.replaceAll('-', ' ')).replaceAll('Chd', 'CHD');
      }
    }
    if (sectionNum === 3) {
      const parentContentTitle = document.getElementById('parent-content-title')?.textContent ?? '';
      if (parentContentTitle !== '') {
        siteSection = parentContentTitle;
      } else {
        [, , , siteSection] = sections;
        siteSection = toTitleCase(siteSection?.replaceAll('-', ' ')).replaceAll('Chd', 'CHD');
      }
    }
  }
  return siteSection;
};

const getLangNameFromLangCode = (langCode: string = 'en') => {
  let langName = '';
  supportedLocales.forEach((langObj: any) => {
    if (langObj.id === langCode) {
      langName = langObj.title;
    }
  });
  return langName;
};

const getPubDateMonthNum = (lang: string, splitPubDate: string[]) => {
  let monthNum: any = '';
  let pubDateMonth: string = '';
  const months: {}[] = getMonthNumAndAbbrevArrByLocale(lang);
  if (lang === 'en') {
    pubDateMonth = splitPubDate[0].toLowerCase();
  } else {
    pubDateMonth = splitPubDate[1].toLowerCase();
  }
  months.forEach((m: {}) => {
    const monthAbbr: string = Object.keys(m)[0].toLowerCase();
    const currMonthNum: any = Object.values(m)[0];
    if (pubDateMonth.includes(monthAbbr)) monthNum = currMonthNum;
  });
  return monthNum;
};

const getPublishDate = (lang: string) => {
  // eslint-disable-next-line operator-linebreak
  const pubDate: string | null | undefined =
    document.getElementById('publish-date')?.textContent;
  if (!pubDate) return '';
  const splitPubDate: string[] | undefined = pubDate
    .split(' ')
    .filter((dateSection) => dateSection.toLowerCase() !== 'de');
  if (splitPubDate === undefined || splitPubDate?.length < 3) return '';
  const pubYear: string = splitPubDate[2];
  const pubMonthNum: string = getPubDateMonthNum(lang, splitPubDate);
  if (!pubMonthNum) return '';
  let pubDay: string = '';
  if (lang === 'en') {
    pubDay = splitPubDate[1].replace(',', '');
  } else {
    pubDay = splitPubDate[0].replace('.', '');
  }
  if (!pubDay) return '';
  if (pubDay.length === 1) pubDay = `0${pubDay}`;
  return pubYear + pubMonthNum + pubDay;
};

const contentDaysSincePublish = (lang: string) => {
  let pubDate: string | number = getPublishDate(lang);
  if (pubDate.length !== 8) return '';
  pubDate = Date.parse(
    `${pubDate.slice(0, 4)}-${pubDate.slice(4, 6)}-${pubDate.slice(6, 8)}`,
  );
  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  const currDateTime: number = new Date().getTime();
  return Math.floor((currDateTime - pubDate) / millisecondsPerDay);
};

const getContentAuthorData = (dataType:string) => {
  let authorData: any = document.getElementById('content-hosts')?.textContent ?? '';
  if (!authorData) return '';
  let returnData: any = [];
  const titleOptions: string[] = ['Ph.D.', 'P.E.', 'J.D.', 'M.D.', 'Dr.', 'FAAP'];
  authorData = authorData?.split(': ')[1].split(', ');
  if (!authorData) return '';
  if (authorData.length === 1) {
    if (dataType === 'name') return authorData[0];
    if (dataType === 'title') {
      titleOptions.forEach((titleOption:string) => {
        if (authorData[0].includes(titleOption)) returnData.push(titleOption);
      });
    }
  } else if (authorData.length > 1) {
    if (dataType === 'name') {
      authorData.forEach((nameOrTitle:string) => {
        if (!titleOptions.includes(nameOrTitle) && nameOrTitle !== 'Jr.') {
          // account for 'Robert F. Kennedy, Jr.'
          if (nameOrTitle === 'Robert F. Kennedy') {
            returnData.push('Robert F. Kennedy, Jr.');
          } else returnData.push(nameOrTitle);
        }
      });
    }
    if (dataType === 'title') {
      authorData.forEach((nameOrTitle:string) => {
        if (nameOrTitle.includes('Dr. ')) returnData.push('Dr.');
        if (titleOptions.includes(nameOrTitle)) {
          returnData.push(nameOrTitle);
        }
      });
    }
  }

  const customSortCompareByLastName = (a:string, b:string) => {
    const aLastNameIndex = a.split(' ').length - 1;
    const bLastNameIndex = b.split(' ').length - 1;
    return a.split(' ')[aLastNameIndex].localeCompare(b.split(' ')[bLastNameIndex]);
  };

  // sort and make unique
  returnData = [...new Set(returnData.sort((a:string, b:string) => customSortCompareByLastName(a, b)))].join('|');
  return returnData;
};

const collectAndPushDataToGtm = (
  locationData: any,
  lang: string,
  localesArr: any,
) => {
  const windowObj: any = window;
  let { dataLayer } = windowObj;
  if (!dataLayer) dataLayer = [];

  const dataLayerObj = {
    contentAuthor: getContentAuthorData('name'),
    contentAuthorTitle: getContentAuthorData('title'),
    contentDaysSincePublishDt: contentDaysSincePublish(lang),
    contentLengthTitle: getContentTitleLength(),
    contentPublishDt: getPublishDate(lang),
    contentTitle: getContentTitle(),
    event: 'pageView',
    hostname: locationData.hostname,
    langCode: lang,
    langName: getLangNameFromLangCode(lang),
    pagePath: getEnglishPagePath(locationData, lang, localesArr),
    pagePathLocal: removeTrailingSlash(locationData.pathname),
    pageURL: getEnglishPageUrl(locationData, lang, localesArr),
    pageURLLocal: removeTrailingSlash(locationData.href),
    pageTitle: getPageTitle(locationData),
    pageViewType: 'virtual',
    siteSection1: 'CHD-TV',
    siteSection2: getSiteSection(2, locationData.pathname.split('/'), lang),
    siteSection3: getSiteSection(3, locationData.pathname.split('/'), lang),
  };

  dataLayer.push(dataLayerObj);
};

const waitForDOMtoLoad = (locationData: any, lang: string, localesArr: any) => {
  document.addEventListener('readystatechange', (event: any) => {
    if (event.target.readyState === 'complete') {
      collectAndPushDataToGtm(locationData, lang, localesArr);
    }
  });
};

export const pushToCustomDataLayer = (
  locationData: any,
  lang: string,
  localesArr: any,
) => {
  if (document.readyState === 'complete') {
    setTimeout(
      () => collectAndPushDataToGtm(locationData, lang, localesArr),
      1000,
    );
  } else {
    waitForDOMtoLoad(locationData, lang, localesArr);
  }
};
